import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { DEFAULT_APP_CONTEXT } from '../constants';
import { CurrentUserSite, CustomerUserAccountType } from '../graphql/generated';
import {
  AppContextType,
  ContractCreationBlockedReason,
  CurrentUser,
  ModuleName,
  OrderFlow,
  PageBackground,
  TFormValues,
} from '../types';
import {
  businessAccount,
  completeAddress,
  getDefaultSite,
  getFullName,
  getValidDeliveryAddress,
} from '../utils/utils';

export const AppContext = createContext<AppContextType>(DEFAULT_APP_CONTEXT);

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within a AppContextProvider');
  }
  return context;
};

type AppContextProviderProps = {
  initialValues?: AppContextType;
  children: React.ReactNode;
};

export const AppContextProvider = ({
  children,
  initialValues = DEFAULT_APP_CONTEXT,
}: AppContextProviderProps) => {
  const [user, setUser] = useState<CurrentUser | null>(initialValues.user);
  const [orderFlow, setOrderFlow] = useState<OrderFlow | null>(initialValues.orderFlow || null);
  const [currentModal, setCurrentModal] = useState<ReactNode>();

  const [userProcessing, setUserProcessing] = useState<boolean>(initialValues.userProcessing);
  const [formValues, setFormValues] = useState<TFormValues>(initialValues.formValues);
  const [orderComplete, setOrderComplete] = useState<boolean>(initialValues.orderComplete);
  const [customBillingAddressState, setCustomBillingAddressState] = useState<boolean>(
    initialValues.customBillingAddress,
  );
  const [selectedSite, setSelectedSite] = useState<CurrentUserSite | null>(
    initialValues.selectedSite,
  );
  const [contractCreationBlocked, setContractCreationBlocked] =
    useState<ContractCreationBlockedReason | null>(null);
  const [offerRequiresManualAddress, setOfferRequiresManualAddress] = useState<boolean>(
    initialValues.offerRequiresManualAddress,
  );
  const [accountType, setAccountType] = useState<CustomerUserAccountType>(
    CustomerUserAccountType.PrivateCustomer,
  );
  const [currentBackground, setCurrentBackground] = useState<PageBackground | undefined>();

  const [modules, setModules] = useState<Set<ModuleName>>(new Set(initialValues.modules));

  const toggleModule = (module: ModuleName) => {
    const newModules = new Set(modules);
    if (modules.has(module)) {
      newModules.delete(module);
    } else {
      newModules.add(module);
    }
    setModules(newModules);
  };
  const addModule = (module: ModuleName) => setModules(new Set(modules).add(module));
  const removeModule = (module: ModuleName) => {
    const newModules = new Set(modules);
    newModules.delete(module);
    setModules(newModules);
  };

  const setCustomBillingAddress = (value: boolean) => {
    if (value && Object.entries(formValues.billingAddress).length === 0) {
      setFormValues({
        ...formValues,
        billingAddress: completeAddress(formValues.deliveryAddress),
      });
    }
    setCustomBillingAddressState(value);
  };

  useEffect(() => {
    if (!user) return;

    setAccountType(user.account.type);

    const firstSiteWithBatteryAndNoContract = getDefaultSite(user);

    const newFormValues = {
      ...formValues,
      accountOwner: getFullName(user?.contact),
      personalData: {
        ...formValues.personalData,
        firstName: user.contact.firstName ?? '',
        lastName: user.contact.lastName ?? '',
        email: user.contact.email ?? '',
        phone: user.contact.phone ?? '',
      },
      deliveryAddress: getValidDeliveryAddress(user, firstSiteWithBatteryAndNoContract),
      billingAddress: {
        postalCode: user.account.billingAddress.postalCode ?? '',
        city: user.account.billingAddress.city ?? '',
        streetName: user.account.billingAddress.streetName ?? '',
        streetNumber: user.account.billingAddress.streetNumber ?? '',
        country: user.account.billingAddress.country ?? '',
      },
    };

    if (businessAccount(user.account.type)) {
      newFormValues.personalData.companyName = user.account.companyName ?? '';
    }

    setFormValues(newFormValues);

    if (firstSiteWithBatteryAndNoContract) {
      setSelectedSite(firstSiteWithBatteryAndNoContract);
    }
  }, [user]);

  const value: AppContextType = useMemo(() => {
    return {
      accountType,
      addModule,
      currentBackground,
      setCurrentBackground,
      formValues,
      modules,
      orderFlow,
      orderComplete,
      offerRequiresManualAddress,
      contractCreationBlocked,
      setContractCreationBlocked,
      removeModule,
      selectedSite,
      setAccountType,
      setFormValues,
      setModules,
      setOfferRequiresManualAddress,
      setOrderComplete,
      setSelectedSite,
      setUser,
      setUserProcessing,
      toggleModule,
      user,
      userProcessing,
      customBillingAddress: customBillingAddressState,
      setCustomBillingAddress,
      setOrderFlow,
      currentModal,
      setCurrentModal,
    };
  }, [
    accountType,
    addModule,
    formValues,
    modules,
    offerRequiresManualAddress,
    contractCreationBlocked,
    orderComplete,
    orderFlow,
    removeModule,
    selectedSite,
    setAccountType,
    setModules,
    setFormValues,
    setOfferRequiresManualAddress,
    setContractCreationBlocked,
    setOrderComplete,
    setSelectedSite,
    setUser,
    setUserProcessing,
    user,
    userProcessing,
    customBillingAddressState,
    setCustomBillingAddress,
    setOrderFlow,
    currentModal,
    setCurrentModal,
  ]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
